import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { 
  Activity, 
  UserCheck, 
  Music, 
  Globe, 
  Video, 
  Image,
  MapPin,
  Loader2
} from 'lucide-react';
import type { ExtendedPost } from '../../types/index';
import type { Country } from './CountryFilterPopup';
import { postService } from '@/lib/supabase-service';
import { useAuth } from '../../lib/auth';
import { toast } from 'react-hot-toast';
import StoryView from "./StoryView";
import PostCard from "../postcard/PostCard";
import PostView from "../cards/PostView";
import TrendingHashtags from "./TrendingHashtags";
import BoostedProfile from "../BoostedProfile"; 
import CountryFilterPopup from "./CountryFilterPopup";
import MusicCarousel from './MusicCarousel';
import PagesCarousel from './PagesCarousel';
import VideosCarousel from './VideosCarousel';
import StoriesCarousel from './StoriesCarousel';
import { Button } from "../ui/button";
import { demoPosts } from '../../utils/demoPostsData';

// Constants for feed configuration
const POSTS_PER_BATCH = 15; // Number of posts to load per batch
const INITIAL_POSTS_COUNT = 15; // Initial number of posts to show

// Tab definitions for the feed navigation
const TABS = [
  { id: "all", label: "All", icon: Activity },
  { id: "following", label: "Following", icon: UserCheck },
  { id: "nearby", label: "Nearby", icon: MapPin },
  { id: "photo", label: "Photo", icon: Image },
  { id: "video", label: "Video", icon: Video },
  { id: "music", label: "Music", icon: Music },
  { id: "countries", label: "Countries", icon: Globe },
];

export default function Feed() {
  const [posts, setPosts] = useState<ExtendedPost[]>([]);
  // State for active tab in the feed navigation
  const [activeTab, setActiveTab] = useState<'all' | 'following' | 'nearby' | 'photo' | 'video' | 'music' | 'countries'>('all');
  // Loading state for initial data fetch
  const [loading, setLoading] = useState(true); // Start with loading true
  const [loadingMore, setLoadingMore] = useState(false);
  // Error message state for potential API errors
  const [errorMessage, setError] = useState<string | null>(null);
  const [selectedPost, setSelectedPost] = useState<ExtendedPost | null>(null);
  const [isPostViewOpen, setIsPostViewOpen] = useState(false);
  // Using state for options popup that will be implemented later
  const [isCountryFilterOpen, setIsCountryFilterOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<Country | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const { user } = useAuth();

  const [visiblePostsCount, setVisiblePostsCount] = useState<number>(INITIAL_POSTS_COUNT); // Initial number of posts to show
  const [hasMore, setHasMore] = useState<boolean>(true);

  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null);
  const videoPostCountRef = useRef<number>(0); // Use ref to avoid closure issues

  // Helper function to get country from post location
  const getPostCountry = useCallback((post: ExtendedPost): Country | undefined => {
    if (!post.users?.location) return undefined;
    
    let countryName: string | undefined;
    
    if (typeof post.users.location === 'string') {
      // Attempt to parse country if location is a simple string like "City, Country"
      const parts = post.users.location.split(',');
      countryName = parts.length > 1 ? parts[parts.length - 1].trim() : parts[0].trim();
    } else {
      countryName = post.users.location.address?.country;
    }
    
    if (!countryName) return undefined;
    
    // Create a basic Country object (in a real app, you'd have a proper country mapping)
    return {
      name: countryName,
      code: countryName.substring(0, 2).toUpperCase(), // Simple fallback code
      flag: '🌍' // Default flag emoji
    };
  }, []);

  // Create filtered arrays from real posts for specific tabs
  const photoPostsData = useMemo(() => 
    posts.filter((post: ExtendedPost) => post.type === 'image'), [posts]
  );

  const videoPostsData = useMemo(() => 
    posts.filter((post: ExtendedPost) => post.type === 'video'), [posts]
  );

  const musicPostsData = useMemo(() => 
    posts.filter((post: ExtendedPost) => post.type === 'music'), [posts]
  );

  const handlePostClick = useCallback((post: ExtendedPost) => {
    setSelectedPost(post);
    setIsPostViewOpen(true);
  }, [setSelectedPost, setIsPostViewOpen]);

  const handleOptionsClick = useCallback((post: ExtendedPost) => {
    setSelectedPost(post);
    // setIsOptionsPopupOpen(true);
  }, [setSelectedPost]);

  // Helper function to render a standard post item - moved inside component and memoized
  const renderPostItem = useCallback((post: ExtendedPost) => {
    const isMusic = post.type === "music" || post.type === "multi-song";
    let orientation: 'portrait' | 'landscape' | undefined = undefined;

    if (post.type === 'video') {
      orientation = videoPostCountRef.current % 2 === 0 ? 'portrait' : 'landscape';
      videoPostCountRef.current++;
    }

    return (
      <PostCard
        key={post.id}
        post={post}
        mediaItems={post.mediaItems}
        songItems={isMusic ? post.songItems || [] : undefined}
        videoOrientation={orientation}
        onClick={() => handlePostClick(post)}
        onOptionsClick={() => handleOptionsClick(post)}
      />
    );
  }, [handlePostClick, handleOptionsClick]);

  const loadPosts = useCallback(async (showRefreshIndicator = false) => {
    try {
      if (showRefreshIndicator) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setError(null);

      console.log('🔄 Loading posts from database...');
      
      // Use the fetchPosts utility function which includes user data and media items
      const fetchedPosts = await postService.getFeedPosts(20, 0);

      console.log('✅ Posts loaded successfully:', fetchedPosts?.length || 0);
      
      // Use demo data as fallback if no real posts are found
      if (!fetchedPosts || fetchedPosts.length === 0) {
        console.log('📝 No real posts found, using demo data as fallback');
        setPosts(demoPosts);
      } else {
        setPosts(fetchedPosts);
      }
      
    } catch (err) {
      console.error('❌ Error loading posts:', err);
      console.log('📝 Error occurred, using demo data as fallback');
      setPosts(demoPosts);
      setError('Failed to load posts. Showing demo content.');
      toast.error('Failed to load posts, showing demo content');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (user) {
      loadPosts();
    }
  }, [user, loadPosts]);

  // Refresh posts manually
  const handleRefresh = useCallback(() => {
    loadPosts(true);
  }, [loadPosts]);

  // Filter posts based on active tab and selected country
  const filteredPosts = useMemo(() => {
    if (!posts.length) return [];

    let filteredPosts = [...posts];
    
    // Apply tab filters
    if (activeTab === "all") {
      // For "all" tab, show all posts without filtering by type
      if (selectedCountry) {
        filteredPosts = filteredPosts.filter(post => {
          const postCountry = getPostCountry(post);
          return postCountry && postCountry.name.toLowerCase().includes(selectedCountry.name.toLowerCase());
        });
      }
    } else if (activeTab === "following") {
      filteredPosts = filteredPosts.filter(post => post.users?.is_followed);
    } else if (activeTab === "nearby") {
      // In a real app, this would filter by geolocation
      filteredPosts = filteredPosts.filter(post => post.users?.location);
    } else if (activeTab === "photo") {
      // Use the photo posts array instead of filtering
      return photoPostsData.slice(0, visiblePostsCount);
    } else if (activeTab === "video") {
      // Use the video posts array instead of filtering
      return videoPostsData.slice(0, visiblePostsCount);
    } else if (activeTab === "music") {
      // Use the music posts array instead of filtering
      return musicPostsData.slice(0, visiblePostsCount);
    } else if (activeTab === "countries") {
      if (selectedCountry) {
        filteredPosts = filteredPosts.filter(post => {
          const postCountry = getPostCountry(post);
          if (postCountry) {
            return postCountry.name.toLowerCase().includes(selectedCountry.name.toLowerCase());
          }
          return false;
        });
      } else {
        // If no country is selected, show all posts
        return posts.slice(0, visiblePostsCount);
      }
    }
    
    // Only show the number of posts based on the current visible count
    return filteredPosts.slice(0, visiblePostsCount);
  }, [posts, activeTab, selectedCountry, visiblePostsCount, photoPostsData, videoPostsData, musicPostsData]);

  // Reset visible posts count and hasMore when tab changes or posts are refetched
  useEffect(() => {
    setVisiblePostsCount(INITIAL_POSTS_COUNT); // Reset to initial count
    setHasMore(filteredPosts.length > INITIAL_POSTS_COUNT);
    videoPostCountRef.current = 0; // Reset video count when tab changes
  }, [activeTab, posts, filteredPosts.length]);

  // Load more posts for infinite scroll - optimized with proper dependencies
  const handleLoadMore = useCallback(() => {
    if (loadingMore || !hasMore) return; // Prevent multiple simultaneous loads
    
    // Simulate loading more posts
    setLoadingMore(true);
    const timeoutId = setTimeout(() => {
      // Increase the number of visible posts by the batch size
      const newVisibleCount = visiblePostsCount + POSTS_PER_BATCH;
      setVisiblePostsCount(newVisibleCount);
      setHasMore(posts.length > newVisibleCount);
      setLoadingMore(false);
    }, 1000);

    return () => clearTimeout(timeoutId); // Return cleanup function
  }, [visiblePostsCount, posts.length, loadingMore, hasMore]);

  // Setup and cleanup intersection observer - improved cleanup
  useEffect(() => {
    const currentLoadMoreTrigger = loadMoreTriggerRef.current;

    if (currentLoadMoreTrigger && hasMore && !loading) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && !loadingMore) {
            handleLoadMore();
          }
        },
        { threshold: 0.5, rootMargin: '100px' } // Add rootMargin for better UX
      );
      
      observer.observe(currentLoadMoreTrigger);
      observerRef.current = observer;

      return () => {
        observer.disconnect(); // Use disconnect for complete cleanup
        observerRef.current = null;
      };
    }
  }, [handleLoadMore, hasMore, loading, loadingMore]);

  // Memoize boosted post creation to prevent recreation on every render
  const createBoostedPost = useCallback((basePost: ExtendedPost, boostType: number): ExtendedPost => {
    // Clone the post and mark it as boosted
    const boostedPost: ExtendedPost = {
      ...basePost,
      id: `boosted-${basePost.id}-${boostType}`,
      is_boosted: true,
      users: {
        ...basePost.users,
        verified: true // Boosted posts are from verified accounts
      }
    };
    
    // Alternate between different types of boosted posts and provide appropriate media
    switch (boostType % 5) {
      case 0: // Image post
        boostedPost.type = 'image';
        boostedPost.caption = 'Check out this amazing photo! #photography #trending';
        boostedPost.mediaItems = [{ 
          id: 'boosted-img-1', 
          url: 'https://cdn.pixabay.com/photo/2015/04/23/22/00/tree-736885_1280.jpg', 
          type: 'image' as const 
        }];
        break;
      case 1: // Video post
        boostedPost.type = 'video';
        boostedPost.caption = 'New video just dropped! Watch now #video #trending';
        boostedPost.mediaItems = [{ 
          id: 'boosted-vid-1', 
          url: 'https://cdn.pixabay.com/video/2019/06/26/24077-346043893_large.mp4', 
          type: 'video' as const 
        }];
        break;
      case 2: // Music post
        boostedPost.type = 'music';
        boostedPost.caption = 'Listen to my latest track! #music #newrelease';
        boostedPost.songItems = [{
          id: 'boosted-song-1',
          url: 'https://cdn.pixabay.com/audio/2022/11/13/audio_cb4f1212a9.mp3',
          title: 'Summer Vibes',
          artist: 'DJ Boosted',
          cover_image: 'https://cdn.pixabay.com/photo/2015/05/15/14/50/concert-768722_1280.jpg',
          duration: '3:24'
        }];
        break;
      case 3: // Shop post
        boostedPost.type = 'shop';
        boostedPost.caption = 'New items in my shop! Check them out #shopping #deals';
        boostedPost.mediaItems = [{ 
          id: 'boosted-shop-1', 
          url: 'https://cdn.pixabay.com/photo/2016/11/22/19/08/hangers-1850082_1280.jpg', 
          type: 'image' as const 
        }];
        break;
      case 4: // Poll post
        boostedPost.type = 'poll';
        boostedPost.caption = 'What do you think? Vote now! #poll #opinion';
        boostedPost.mediaItems = [{ 
          id: 'boosted-poll-1', 
          url: 'https://cdn.pixabay.com/photo/2018/01/17/07/06/laptop-3087585_1280.jpg', 
          type: 'image' as const 
        }];
        break;
    }
    
    return boostedPost;
  }, []);

  // Memoize the structured feed creation function
  const createStructuredFeed = useCallback((postsArray: ExtendedPost[]) => {
    if (postsArray.length < 15) {
      // If we don't have enough posts, just render what we have
      return postsArray.map(post => renderPostItem(post));
    }
    
    const elements: JSX.Element[] = [];
    
    // 1. Post
    elements.push(renderPostItem(postsArray[0]));
    
    // 2. Boosted Post
    const boostedPost1 = createBoostedPost(postsArray[1], 0);
    elements.push(
      <PostCard
        key={`boosted-${boostedPost1.id}`}
        post={boostedPost1}
        mediaItems={boostedPost1.mediaItems}
        songItems={boostedPost1.type === "music" || boostedPost1.type === "multi-song" ? boostedPost1.songItems || [] : undefined}
        boosted={true}
        onClick={() => handlePostClick(boostedPost1)}
        onOptionsClick={() => handleOptionsClick(boostedPost1)}
      />
    );
    
    // 3. Post
    elements.push(renderPostItem(postsArray[2]));
    
    // 4. Post
    elements.push(renderPostItem(postsArray[3]));
    
    // 5. People You May Know
    elements.push(
      <div key="people-you-may-know" className="mb-4">
        <h3 className="text-sm font-medium mb-2 px-4 text-gray-700 dark:text-gray-300">
          People you may know
        </h3>
        <div className="px-4">
          <BoostedProfile profiles={[]} />
        </div>
      </div>
    );
    
    // 6. Post
    elements.push(renderPostItem(postsArray[4]));
    
    // 7. Post
    elements.push(renderPostItem(postsArray[5]));
    
    // 8. Post
    elements.push(renderPostItem(postsArray[6]));
    
    // 9. Carousel *Alternate each load - improved alternation logic
    // Alternate between different types of carousels for variety
    const loadCount = Math.floor(visiblePostsCount / POSTS_PER_BATCH);
    const showMusicCarousel = loadCount % 3 === 0;
    const showVideosCarousel = loadCount % 3 === 1;
    const showStoriesCarousel = loadCount % 3 === 2;

    if (showMusicCarousel) {
      elements.push(
        <div key="music-carousel" className="mb-4">
          <MusicCarousel songs={[]} />
        </div>
      );
    } else if (showVideosCarousel) {
      elements.push(
        <div key="videos-carousel" className="mb-4">
          <VideosCarousel videos={[]} />
        </div>
      );
    } else if (showStoriesCarousel) {
      elements.push(
        <div key="stories-carousel" className="mb-4">
          <StoriesCarousel stories={[]} />
        </div>
      );
    } else {
      elements.push(
        <div key="pages-carousel" className="mb-4">
          <PagesCarousel profiles={[]} />
        </div>
      );
    }
    
    // 10. Post
    elements.push(renderPostItem(postsArray[7]));
    
    // 11. Post
    elements.push(renderPostItem(postsArray[8]));
    
    // 12. Boosted Post
    const boostedPost2 = createBoostedPost(postsArray[9], 1);
    elements.push(
      <PostCard
        key={`boosted-${boostedPost2.id}`}
        post={boostedPost2}
        mediaItems={boostedPost2.mediaItems}
        songItems={boostedPost2.type === "music" || boostedPost2.type === "multi-song" ? boostedPost2.songItems || [] : undefined}
        boosted={true}
        onClick={() => handlePostClick(boostedPost2)}
        onOptionsClick={() => handleOptionsClick(boostedPost2)}
      />
    );
    
    // 13. Post
    elements.push(renderPostItem(postsArray[10]));
    
    // 14. Post
    elements.push(renderPostItem(postsArray[11]));
    
    // 15. Post
    elements.push(renderPostItem(postsArray[12]));
    
    // 16. Additional posts if available (for infinite scroll)
    if (postsArray.length > 15) {
      for (let i = 13; i < Math.min(postsArray.length, visiblePostsCount); i++) {
        elements.push(renderPostItem(postsArray[i]));
      }
    }
    
    return elements;
  }, [renderPostItem, createBoostedPost, handlePostClick, handleOptionsClick, visiblePostsCount]);

  // Memoize feed elements generation for optimal performance
  const feedElements = useMemo(() => {
    const elements: JSX.Element[] = [];
    
    // Apply the structured feed to the appropriate tab
    if (activeTab === "photo") {
      elements.push(...createStructuredFeed(photoPostsData));
    } else if (activeTab === "video") {
      elements.push(...createStructuredFeed(videoPostsData));
    } else if (activeTab === "music") {
      elements.push(...createStructuredFeed(musicPostsData));
    } else if (filteredPosts.length >= 15) {
      elements.push(...createStructuredFeed(filteredPosts));
    } else {
      // Fallback if we don't have enough posts
      filteredPosts.forEach(post => {
        elements.push(renderPostItem(post));
      });
    }
    
    return elements;
  }, [activeTab, photoPostsData, videoPostsData, musicPostsData, filteredPosts, createStructuredFeed, renderPostItem]);

  if (loading && posts.length === 0) { // Show initial full page loading state
    return (
      <div className="max-w-full mx-auto py-16 md:py-6 px-4 bg-white dark:bg-black min-h-screen">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 py-4">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="animate-pulse">
              <div className="aspect-[4/5] bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="max-w-full mx-auto py-16 md:py-6 px-4 bg-white dark:bg-black min-h-screen flex justify-center items-center">
        <p className="text-red-500 dark:text-red-400 text-lg">{errorMessage}</p>
      </div>
    );
  }

  return (
    <div className="max-w-full mx-auto py-16 md:py-6 px-4 bg-white dark:bg-black">
      <div className="flex flex-col md:flex-row md:justify-center gap-6">
        {/* Main feed column */}
        <div className="max-w-lg mx-auto w-full py-2 space-y-3">
          <div className="mb-1">
            <StoryView />
          </div>

          <div className="lg:hidden mb-4">
            <TrendingHashtags hashtags={[]} />
          </div>

          {/* Refresh Button */}
          <div className="flex justify-between items-center mb-2">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Feed
            </h2>
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="flex items-center space-x-2 px-3 py-1 text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50 dark:text-blue-400 dark:hover:text-blue-300"
            >
              <svg 
                className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span>{refreshing ? 'Refreshing...' : 'Refresh'}</span>
            </button>
          </div>

          <div className="flex justify-between overflow-x-auto scrollbar-hide w-full mb-2 sticky top-16 md:top-0 bg-white dark:bg-black py-2 z-10">
            {TABS.map((tab) => {
              const IconComponent = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => {
                    if (tab.id === "countries") {
                      setIsCountryFilterOpen(true);
                    } else {
                      setActiveTab(tab.id as 'all' | 'following' | 'nearby' | 'photo' | 'video' | 'music' | 'countries');
                      setSelectedCountry(null);
                    }
                  }}
                  className={`flex flex-col items-center gap-1 p-2 rounded-full transition-colors duration-200 min-w-[60px] ${
                    activeTab === tab.id
                      ? 'bg-[#065900] text-white'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-[#065900] hover:text-white active:bg-[#054700] active:text-white'
                  }`}
                >
                  <IconComponent className="h-5 w-5" data-component-name="Feed" />
                </button>
              );
            })}
          </div>
          {filteredPosts.length === 0 && !loading ? (
            <div className="text-center py-10">
              <p className="text-gray-600 dark:text-gray-400">
                No posts to display for "{activeTab}"
              </p>
            </div>
          ) : (
            <div className="mb-1 px-4 md:px-0">
              <div className="pb-16">
                {feedElements.length > 0 ? (
                  <div className="space-y-4">
                    {feedElements}
                    {hasMore && (
                      <div className="flex justify-center py-4">
                        <Button
                          variant="outline"
                          onClick={handleLoadMore}
                          disabled={loadingMore}
                          className="w-full max-w-md mx-4"
                        >
                          {loadingMore ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Loading...
                            </>
                          ) : (
                            "Load more"
                          )}
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-10">
                    <p className="text-gray-600 dark:text-gray-400">
                      No posts to display for "{activeTab}"
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Invisible trigger for loading more posts */}
          {hasMore && !loadingMore && filteredPosts.length > 0 && (
            <div ref={loadMoreTriggerRef} style={{ height: '50px', margin: '20px 0' }} />
          )}

          {!hasMore && filteredPosts.length > 0 && (
            <div className="text-center py-6 text-gray-500 dark:text-gray-400 text-sm">
              You've reached the end of the feed.
            </div>
          )}
        </div>

        {/* Right sidebar for larger screens */}
        <div className="hidden lg:block w-80 space-y-6 sticky top-20 h-[calc(100vh-5rem)] overflow-y-auto pb-20 hide-scrollbar">
          <TrendingHashtags hashtags={[]} />
          <BoostedProfile profiles={[]} />
        </div>
      </div>

      {/* Post view modal */}
      {isPostViewOpen && selectedPost && (
        <PostView
          isOpen={isPostViewOpen}
          onClose={() => setIsPostViewOpen(false)}
          post={{
            id: selectedPost.id,
            caption: selectedPost.caption || '',
            media_url: selectedPost.mediaItems?.[0]?.url || '',
            created_at: selectedPost.created_at || new Date().toISOString(),
            likes_count: selectedPost.like_count || 0,
            comments_count: selectedPost.comment_count || 0,
            bookmarks_count: 0,
            gifts_count: 0,
            views_count: 0,
            users: {
              username: selectedPost.users?.username || 'Unknown User',
              avatar_url: selectedPost.users?.avatar_url || '',
              verified: selectedPost.users?.verified || false
            },
            comments: selectedPost.comments || []
          }}
        />
      )}

      {/* Country filter popup */}
      {isCountryFilterOpen && (
        <CountryFilterPopup
          isOpen={isCountryFilterOpen}
          onClose={() => setIsCountryFilterOpen(false)}
          onSelectCountry={(country: Country | null) => {
            // Handle both selection and clearing in one callback
            setSelectedCountry(country);
            setIsCountryFilterOpen(false);
          }}
          selectedCountry={selectedCountry}
        />
      )}
    </div>
  );
}